import { useMutation, useQuery } from '@apollo/client';
import { Action, PriceList } from 'models/action';
import { ActionSet } from 'models/action-set';
import {
    MaterialPriceDifference,
    Organization,
    ExtendedMaterialGroup,
    MaterialGroup,
    OrgActionSet,
} from 'models/organization';
import { ReactNode, createContext, createElement, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

import {
    enableDisableConnectedLabTemplate,
    requireExceptionConnectedLabTemplate,
    updateMaterialPriceDiffTemplate,
    updatePriceListTemplate,
    deletePriceListTemplate,
    toggleTioTandvardEnabledTemplate,
    updateConnectedLabCurrencyTemplate,
} from '../apollo/templates/organization/mutation.graphql';
import { ConnectedLabForClinicTemplate } from '../apollo/templates/organization/query.graphql';

type Category = {
    _id: string;
    name: string;
    actionSet: string;
    isSelected: boolean;
};

type EditLabContextType = {
    lab: Organization;
    isDisabled: boolean;
    isDisableLoading: boolean;
    isTioTandvardEnabled: boolean;
    isTandvardEnabledLoading: boolean;
    isRequireException: boolean;
    isRequireExceptionLoading: boolean;
    currency: string | null;
    defaultActionSetCurrency: string | null;
    isCurrencyLoading: boolean;
    updateConnectedLabCurrency: (currency: string) => Promise<void>;
    actionSets: ActionSet[];
    activeActionSet: string;
    actionSetActiveForDisplay: ActionSet;
    materialGroups: ExtendedMaterialGroup[];
    materialPriceDifference: MaterialPriceDifference[];
    categories: Category[];
    selectedCategory: string;
    setSelectedCategory: (id: string, isAlreadySet: boolean) => void;
    isAlreadySetCategory: boolean;
    priceList: PriceList[];
    isLoading: boolean;
    isMaterialGroupsLoading: boolean;
    isPriceListLoading: boolean;
    updatePriceListLoading: boolean;
    error: string | null;
    refetch: (vars?: any) => Promise<any>;
    toggleLabStatus: (isDisabled: boolean) => Promise<void>;
    toggleRequireException: (isRequireException: boolean) => Promise<void>;
    toggleTioTandvardEnabled: (isTioTandvardEnabled: boolean) => Promise<void>;
    setActiveActionSet: (id: string) => void;
    updateMaterialPriceDiff: (
        materialGroupId: string,
        materials: { material: string; priceDiff: string | null; isAvailable: boolean }[],
    ) => Promise<void>;
    updatePriceList: (actionSetId: string, categoryId: string, actions: Action[], rank: number) => Promise<void>;
    deletePriceList: (actionSetId: string, categoryId: string) => Promise<void>;
    deletePriceListLoading: boolean;
};

const EditLabContext = createContext<EditLabContextType | undefined>(undefined);

export const EditLabProvider = ({ children, labId }: { children: ReactNode; labId: string }) => {
    const [actionsetId, setActionSetId] = useState<string>();
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [isAlreadySetCategory, setIsAlreadySetCategory] = useState(false);
    const [isMaterialGroupsLoading, setIsMaterialGroupsLoading] = useState(false);
    const [isPriceListLoading, setIsPriceListLoading] = useState(false);
    const preservedLabData = useRef<Organization | null>(null);
    const preservedIsRequireException = useRef<boolean | null>(null);
    const preservedIsTandvardEnabled = useRef<boolean | null>(null);

    const { showBoundary } = useDenthubErrorBoundary();

    const getVars = useCallback((override?: any) => ({ labId, ...override }), [labId]);

    const { data, loading, error, refetch, fetchMore } = useQuery(ConnectedLabForClinicTemplate, {
        variables: getVars({
            ...(actionsetId && { actionsetId }),
            // ...(selectedCategory && { categoryId: selectedCategory }),
        }),
        skip: !labId,
        fetchPolicy: 'network-only',
    });

    const clinicData = data?.connectedLabForClinic;
    const connectedLab = clinicData?.connectedLab;

    if (connectedLab?.lab) preservedLabData.current = connectedLab.lab;
    if (connectedLab?.isRequireException) preservedIsRequireException.current = connectedLab.isRequireException;
    if (connectedLab?.isTioTandvardEnabled) preservedIsTandvardEnabled.current = connectedLab.isTioTandvardEnabled;

    const [enableDisableLab, { loading: isDisableLoading }] = useMutation(enableDisableConnectedLabTemplate, {
        onError: showBoundary,
    });

    const [requireExceptionLab, { loading: isRequireExceptionLoading }] = useMutation(
        requireExceptionConnectedLabTemplate,
        { onError: showBoundary },
    );

    const [toggleTioTandvardEnabledMutation, { loading: isTandvardEnabledLoading }] = useMutation(
        toggleTioTandvardEnabledTemplate,
        {
            onError: showBoundary,
        },
    );

    const [updateConnectedLabCurrencyMutation, { loading: isCurrencyLoading }] = useMutation(
        updateConnectedLabCurrencyTemplate,
        {
            onError: showBoundary,
        },
    );

    const [updateMaterialPriceDiffMutation] = useMutation(updateMaterialPriceDiffTemplate, { onError: showBoundary });

    const [updatePriceListMutation, { loading: updatePriceListLoading }] = useMutation(updatePriceListTemplate, {
        onError: showBoundary,
    });

    const [deletePriceListMutation, { loading: deletePriceListLoading }] = useMutation(deletePriceListTemplate, {
        onError: showBoundary,
    });

    // Derived Data
    const materialGroups: ExtendedMaterialGroup[] = useMemo(() => {
        const mg = clinicData?.materialGroups || [];
        const mpd = connectedLab?.materialPriceDifference || [];

        return mg.map((group: MaterialGroup) => ({
            ...group,
            materialPriceDifferenceForGroup: mpd
                .filter(
                    (item: any) =>
                        item.actionSet.toString() === group.actionSet.toString() &&
                        item.materialGroupId.toString() === group._id.toString(),
                )
                .map((item: any) => ({
                    ...item,
                    materials: item.materials.map((m: any) => ({
                        ...m,
                        materialName:
                            group.materials.find((gm: any) => gm._id.toString() === m.material.toString())?.material ||
                            '',
                    })),
                })),
        }));
    }, [clinicData?.materialGroups, connectedLab?.materialPriceDifference]);

    const priceList: PriceList[] = useMemo(() => {
        if (!selectedCategory || !connectedLab?.priceList || !clinicData?.actions) return [];

        return connectedLab.priceList.map((pl: any) => {
            const actions = clinicData.actions.map((action: any) => {
                const existingAction = pl.actions?.find((a: any) => a.action === action._id);

                if (existingAction) {
                    return {
                        ...existingAction,
                        actionObj: { name: action.name, number: action.number, quotable: action.quotable },
                    };
                }

                return {
                    action: action._id,
                    deliveryTime: 0,
                    enable: false,
                    price: 0,
                    tiotandvardPrice: null,
                    actionObj: { name: action.name, number: action.number, quotable: action.quotable },
                };
            });

            return {
                ...pl,
                actions,
            };
        });
    }, [selectedCategory, connectedLab?.priceList, clinicData?.actions]);

    // Handlers
    const toggleLabStatus = useCallback(
        async (isDisabled: boolean) => {
            await enableDisableLab({
                variables: { labId, isDisabled },
                update: (cache, { data }) => {
                    cache.writeQuery({
                        query: ConnectedLabForClinicTemplate,
                        variables: getVars({ actionsetId }),
                        data: {
                            connectedLabForClinic: {
                                ...clinicData,
                                connectedLab: { ...connectedLab, isDisabled: data.updateConnectedLabStatus },
                            },
                        },
                    });
                },
            });
        },
        [enableDisableLab, labId, actionsetId, clinicData, connectedLab, getVars],
    );

    const toggleRequireException = async (isRequireException: boolean) => {
        const result = await requireExceptionLab({
            variables: { labId, isRequireException },
            update: (cache, { data }) => {
                cache.writeQuery({
                    query: ConnectedLabForClinicTemplate,
                    variables: getVars({ actionsetId }),
                    data: {
                        connectedLabForClinic: {
                            ...clinicData,
                            connectedLab: {
                                ...connectedLab,
                                isRequireException: data.updateConnectedLabRequireException,
                            },
                        },
                    },
                });
            },
        });

        preservedIsRequireException.current = result.data.updateConnectedLabRequireException;
    };

    const toggleTioTandvardEnabled = async (isTioTandvardEnabled: boolean) => {
        const result = await toggleTioTandvardEnabledMutation({
            variables: { labId, isTioTandvardEnabled },
            update: (cache, { data }) => {
                cache.writeQuery({
                    query: ConnectedLabForClinicTemplate,
                    variables: getVars({ actionsetId }),
                    data: {
                        connectedLabForClinic: {
                            ...clinicData,
                            connectedLab: {
                                ...connectedLab,
                                isTioTandvardEnabled: data.toggleTioTandvardEnabled,
                            },
                        },
                    },
                });
            },
        });

        preservedIsTandvardEnabled.current = result.data.toggleTioTandvardEnabled;
    };

    const updateConnectedLabCurrency = async (currency: string) => {
        await updateConnectedLabCurrencyMutation({
            variables: { labId, currency },
            update: (cache, { data: mutationData }) => {
                cache.writeQuery({
                    query: ConnectedLabForClinicTemplate,
                    variables: getVars({ actionsetId }),
                    data: {
                        connectedLabForClinic: {
                            ...clinicData,
                            connectedLab: {
                                ...connectedLab,
                                currency: mutationData.updateConnectedLabCurrency,
                            },
                        },
                    },
                });
            },
        });
    };

    const setActiveActionSet = useCallback(
        async (newId: string) => {
            setIsMaterialGroupsLoading(true);
            setActionSetId(newId);
            try {
                await refetch(getVars({ actionsetId: newId }));
            } finally {
                setIsMaterialGroupsLoading(false);
            }
        },
        [refetch, getVars],
    );

    const handleSetSelectedCategory = useCallback(
        async (id: string, isAlreadySet: boolean) => {
            const scrollY = window.scrollY;
            setIsPriceListLoading(true);

            const vars = getVars({ ...(actionsetId && { actionsetId }), categoryId: id });

            try {
                if (data?.connectedLabForClinic) {
                    await fetchMore({
                        variables: vars,
                        updateQuery: (prev, { fetchMoreResult }) => ({
                            ...prev,
                            connectedLabForClinic: {
                                ...prev.connectedLabForClinic,
                                ...fetchMoreResult?.connectedLabForClinic,
                            },
                        }),
                    });
                } else {
                    await refetch(vars);
                }
                setSelectedCategory(id);
                setIsAlreadySetCategory(isAlreadySet);
                window.scrollTo(0, scrollY);
            } finally {
                setIsPriceListLoading(false);
            }
        },
        [fetchMore, refetch, actionsetId, data, getVars],
    );

    const updateMaterialPriceDiff = useCallback(
        async (
            materialGroupId: string,
            materials: { material: string; priceDiff: string | null; isAvailable: boolean }[],
        ) => {
            await updateMaterialPriceDiffMutation({
                variables: {
                    labId,
                    materialGroupId,
                    actionSetId: actionsetId || clinicData?.activeActionSet,
                    materials: materials.map((m) => ({
                        material: m.material,
                        priceDiff: m.priceDiff ? parseFloat(m.priceDiff) : null,
                        isAvailable: m.isAvailable,
                    })),
                },
                refetchQueries: [{ query: ConnectedLabForClinicTemplate, variables: getVars({ actionsetId }) }],
            });
        },
        [updateMaterialPriceDiffMutation, labId, actionsetId, clinicData?.activeActionSet, getVars],
    );

    const updatePriceList = useCallback(
        async (actionSetId: string, categoryId: string, actions: Action[], rank: number) => {
            await updatePriceListMutation({
                variables: { labId, actionSetId, categoryId, actions, rank },
                refetchQueries: [
                    { query: ConnectedLabForClinicTemplate, variables: getVars({ actionsetId, categoryId }) },
                ],
            });
            setIsAlreadySetCategory(true);
        },
        [updatePriceListMutation, labId, actionsetId, getVars],
    );

    const deletePriceList = useCallback(
        async (actionSetId: string, categoryId: string) => {
            await deletePriceListMutation({
                variables: { labId, actionSetId, categoryId },
                refetchQueries: [
                    { query: ConnectedLabForClinicTemplate, variables: getVars({ actionsetId, categoryId }) },
                ],
            });
            setIsAlreadySetCategory(false);
        },
        [deletePriceListMutation, labId, actionsetId, getVars],
    );

    const activeActionSetForDisplay = useMemo(() => {
        if (!clinicData?.actionSets || !clinicData?.activeActionSet) return null;
        return (
            clinicData.actionSets.find((actionSet: OrgActionSet) => actionSet._id === clinicData.activeActionSet) ||
            null
        );
    }, [clinicData?.actionSets, clinicData?.activeActionSet]);

    // Final Value
    const stableValues = useMemo(
        () => ({
            lab: connectedLab?.lab || preservedLabData.current,
            isDisabled: connectedLab?.isDisabled || false,
            isDisableLoading,
            isTioTandvardEnabled: connectedLab?.isTioTandvardEnabled || false,
            isRequireException: connectedLab?.isRequireException || preservedIsRequireException.current || false,
            isRequireExceptionLoading,
            isTandvardEnabledLoading,
            currency: connectedLab?.currency || null,
            defaultActionSetCurrency: activeActionSetForDisplay?.currency || null,
            isCurrencyLoading,
            updateConnectedLabCurrency,
            actionSets: clinicData?.actionSets || [],
            activeActionSet: actionsetId || clinicData?.activeActionSet,
            actionSetActiveForDisplay: activeActionSetForDisplay,
            materialGroups: materialGroups,
            materialPriceDifference: connectedLab?.materialPriceDifference || [],
            categories: clinicData?.categories || [],
            isLoading: loading && !actionsetId,
            isMaterialGroupsLoading,
            error: error?.message || null,
            refetch,
            toggleLabStatus,
            toggleRequireException,
            toggleTioTandvardEnabled,
            setActiveActionSet,
            updateMaterialPriceDiff,
            updatePriceList,
            deletePriceList,
        }),
        [
            connectedLab,
            isDisableLoading,
            isRequireExceptionLoading,
            clinicData,
            materialGroups,
            loading,
            actionsetId,
            isMaterialGroupsLoading,
            error,
            refetch,
            toggleLabStatus,
            toggleRequireException,
            toggleTioTandvardEnabled,
            isCurrencyLoading,
            updateConnectedLabCurrency,
            activeActionSetForDisplay,
            setActiveActionSet,
            updateMaterialPriceDiff,
            updatePriceList,
            deletePriceList,
        ],
    );

    const priceListValues = useMemo(
        () => ({
            selectedCategory,
            setSelectedCategory: handleSetSelectedCategory,
            isAlreadySetCategory,
            priceList,
            isPriceListLoading,
            updatePriceListLoading,
            deletePriceListLoading,
        }),
        [
            selectedCategory,
            handleSetSelectedCategory,
            isAlreadySetCategory,
            priceList,
            isPriceListLoading,
            updatePriceListLoading,
            deletePriceListLoading,
        ],
    );

    return createElement(EditLabContext.Provider, { value: { ...stableValues, ...priceListValues } }, children);
};

export const useEditLab = (): EditLabContextType => {
    const ctx = useContext(EditLabContext);
    if (!ctx) throw new Error('useEditLab must be used within an EditLabProvider');
    return ctx;
};
